import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Loader from "@jumbo/components/Loader";
import { getLeadSampleProducts, updateLead } from "app/redux/slice/leadSlice";

// Names that don't start with a letter are bucketed together at the end, so
// nothing silently disappears from the list.
const groupKeyFor = (name) => {
  const first = String(name || "").trim().charAt(0).toUpperCase();
  return /^[A-Z]$/.test(first) ? first : "#";
};

// The SKU repeats the name for most products ("BARCODE (BARCODE)"), so only
// show it when it actually adds information.
const labelFor = (product) => {
  const name = product?.name || "";
  const sku = product?.prefix || "";
  return sku && sku.toLowerCase() !== name.toLowerCase()
    ? `${name} (${sku})`
    : name;
};

const SampleProducts = ({ leadId, selectedIds, onSaved }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { sampleProducts, sampleProductsLoading } = useSelector(
    (state) => state?.lead
  );

  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(getLeadSampleProducts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The lead's saved ids arrive as ObjectId strings; normalise so includes()
  // comparisons against product._id always line up.
  useEffect(() => {
    setSelected((selectedIds || []).map((id) => String(id)));
  }, [selectedIds]);

  const groups = useMemo(() => {
    const list = Array.isArray(sampleProducts) ? sampleProducts : [];
    const map = list.reduce((acc, product) => {
      const key = groupKeyFor(product?.name);
      if (!acc[key]) acc[key] = [];
      acc[key].push(product);
      return acc;
    }, {});
    // "#" always sorts last, letters A-Z before it.
    return Object.keys(map)
      .sort((a, b) => {
        if (a === "#") return 1;
        if (b === "#") return -1;
        return a.localeCompare(b);
      })
      .map((letter) => ({ letter, products: map[letter] }));
  }, [sampleProducts]);

  const allIds = useMemo(
    () =>
      (Array.isArray(sampleProducts) ? sampleProducts : []).map((p) =>
        String(p._id)
      ),
    [sampleProducts]
  );

  const toggleOne = (id) => {
    const value = String(id);
    setSelected((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  const toggleAll = (checked) => {
    setSelected(checked ? allIds : []);
  };

  const toggleLetter = (products, checked) => {
    const ids = products.map((p) => String(p._id));
    setSelected((prev) =>
      checked
        ? [...prev, ...ids.filter((id) => !prev.includes(id))]
        : prev.filter((id) => !ids.includes(id))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    const response = await dispatch(
      updateLead({ id: leadId, data: { sampleProductIds: selected } })
    ).unwrap();

    const isSuccess = response.status === 200;
    enqueueSnackbar(response.data?.responseMessage, {
      variant: isSuccess ? "success" : "error",
    });
    if (isSuccess) onSaved?.();
    setSaving(false);
  };

  const handleReset = () => {
    setSelected((selectedIds || []).map((id) => String(id)));
  };

  if (sampleProductsLoading) {
    return <Loader />;
  }

  if (!allIds.length) {
    return (
      <Typography variant="body1" sx={{ py: 3, textAlign: "center" }}>
        No products available.
      </Typography>
    );
  }

  const allChecked = selected.length === allIds.length;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="h5" mb={0}>
            Sample Products
          </Typography>
          <Box
            sx={{
              px: 1.25,
              py: 0.25,
              borderRadius: 4,
              bgcolor: selected.length ? "primary.main" : "action.selected",
              color: selected.length ? "common.white" : "text.secondary",
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {selected.length} / {allIds.length} selected
          </Box>
        </Stack>
        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Checkbox
              checked={allChecked}
              indeterminate={selected.length > 0 && !allChecked}
              onChange={(e) => toggleAll(e.target.checked)}
            />
          }
          label={
            <Typography variant="body1" fontWeight={500} mb={0}>
              Select All
            </Typography>
          }
        />
      </Stack>

      <Box
        sx={{
          maxHeight: 520,
          overflowY: "auto",
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        {groups.map(({ letter, products }) => {
          const letterIds = products.map((p) => String(p._id));
          const letterChecked = letterIds.every((id) => selected.includes(id));
          const letterSome = letterIds.some((id) => selected.includes(id));

          return (
            <Box key={letter}>
              {/* Letter header doubles as a per-letter select-all */}
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  px: 2,
                  py: 0.5,
                  bgcolor: "background.default",
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={letterChecked}
                      indeterminate={letterSome && !letterChecked}
                      onChange={(e) => toggleLetter(products, e.target.checked)}
                    />
                  }
                  label={
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color="primary.main"
                      mb={0}
                    >
                      {letter}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        {`  (${products.length})`}
                      </Typography>
                    </Typography>
                  }
                />
              </Box>

              <Grid container sx={{ px: 1, py: 0.5 }}>
                {products.map((product) => {
                  const id = String(product._id);
                  const isChecked = selected.includes(id);
                  const label = labelFor(product);

                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={id}>
                      <FormControlLabel
                        // Clicking the name toggles the checkbox too.
                        control={
                          <Checkbox
                            size="small"
                            checked={isChecked}
                            onChange={() => toggleOne(id)}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            noWrap
                            title={label}
                            sx={{ fontWeight: isChecked ? 600 : 400 }}
                            mb={0}
                          >
                            {label}
                          </Typography>
                        }
                        sx={{
                          width: "100%",
                          m: 0,
                          px: 1,
                          borderRadius: 1,
                          "&:hover": { bgcolor: "action.hover" },
                          // Keeps long names on one line with an ellipsis
                          // instead of wrapping and breaking the grid.
                          "& .MuiFormControlLabel-label": {
                            minWidth: 0,
                            overflow: "hidden",
                          },
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          );
        })}
      </Box>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <LoadingButton
          variant="outlined"
          color="secondary"
          size="large"
          onClick={handleReset}
          disabled={saving}
        >
          Reset
        </LoadingButton>
        <LoadingButton
          variant="contained"
          size="large"
          onClick={handleSave}
          loading={saving}
          loadingIndicator={<CircularProgress size={20} />}
        >
          Save
        </LoadingButton>
      </Stack>
    </Box>
  );
};

export default SampleProducts;
